import React, { Component, ReactNode } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';


interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
  retryCount: number;
}

export class AppCrashBoundary extends Component<Props, State> {
  private maxRetries = 3;

  constructor(props: Props) {
    super(props);
    this.state = { 
      hasError: false, 
      retryCount: 0
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { 
      hasError: true, 
      error
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('App Crash Boundary caught error:', error, errorInfo);
    this.setState({ errorInfo });
    
    // Log error for debugging
    console.warn('Critical app error details:', { error, errorInfo });
  }

  handleRetry = () => {
    if (this.state.retryCount < this.maxRetries) {
      this.setState(prev => ({ 
        hasError: false, 
        error: undefined, 
        errorInfo: undefined,
        retryCount: prev.retryCount + 1
      }));
    } else {
      // Don't auto-refresh to prevent infinite loops
      console.log('Maximum retry attempts reached - manual refresh required');
    }
  };

  handleReset = () => {
    // Clear all local storage and reload
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <Card className="max-w-lg mx-auto">
            <CardHeader>
              <CardTitle className="flex items-center text-destructive">
                <AlertTriangle className="h-6 w-6 mr-3" />
                Application Error
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  The application encountered a critical error and needs to restart.
                  {this.state.error?.message && (
                    <details className="mt-2">
                      <summary className="cursor-pointer text-sm font-medium">
                        Error details
                      </summary>
                      <pre className="mt-2 text-xs bg-muted p-2 rounded overflow-auto">
                        {this.state.error.message}
                      </pre>
                    </details>
                  )}
                </AlertDescription>
              </Alert>

              <div className="text-sm text-muted-foreground">
                Retry attempts: {this.state.retryCount}/{this.maxRetries}
              </div>
              
              <div className="flex gap-3">
                <Button 
                  onClick={this.handleRetry} 
                  variant="default" 
                  size="sm" 
                  className="flex-1"
                  disabled={this.state.retryCount >= this.maxRetries}
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  {this.state.retryCount >= this.maxRetries ? 'Refresh Page' : 'Try Again'}
                </Button>
                <Button 
                  onClick={this.handleReset} 
                  variant="outline" 
                  size="sm" 
                  className="flex-1"
                >
                  <Home className="h-4 w-4 mr-2" />
                  Reset App
                </Button>
              </div>

              <div className="text-xs text-muted-foreground text-center">
                If this issue persists, try clearing your browser data or contact support.
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}